import { ReactGAImplementation } from 'react-ga4';
import type { UaEventOptions } from 'react-ga4/types/ga4';
import { debounce } from 'lodash-es';
import { throwOrLog } from 'utils/env';

const CATEGORY = {
  BottomPanel: 'Bottom panel',
  Tools: 'Tools',
  Canvas: 'Canvas',
  Settings: 'Settings',
  ClientForm: 'ClientForm',
} as const;

export const ReactGA = new ReactGAImplementation();

const asElementTypes = <T>(et: { [K in keyof T]: UaEventOptions }) => et;

const events = asElementTypes({
  // Bottom panel events
  bottomPanelApDeviceChange: {
    category: CATEGORY.BottomPanel,
    action: 'change:apDevice',
    label: '{name}',
  },
  bottomPanelClientDeviceChange: {
    category: CATEGORY.BottomPanel,
    action: 'change:clientDevice',
    label: '{name}',
  },
  bottomPanelRadioChange: {
    category: CATEGORY.BottomPanel,
    action: 'change:radio',
    label: '{variant}',
  },
  bottomPanelChannelChange: {
    category: CATEGORY.BottomPanel,
    action: 'change:channel',
    label: '{variant}:{channel}',
  },
  bottomPanelChannelWidthChange: {
    category: CATEGORY.BottomPanel,
    action: 'change:channelWidth',
    label: '{variant}:{channelWidth}',
  },
  bottomPanelChannelUsageChange: {
    category: CATEGORY.BottomPanel,
    action: 'change:channelUsage',
    label: '{variant}:{channelUsage}',
  },
  bottomPanelAfcChange: {
    category: CATEGORY.BottomPanel,
    action: 'change:afc',
    label: '{afc}',
  },
  bottomPanelMloChange: {
    category: CATEGORY.BottomPanel,
    action: 'change:mlo',
    label: '{mlo}',
  },
  bottomPanelMountToggle: {
    category: CATEGORY.BottomPanel,
    action: 'change:mount',
  },
  bottomPanelOutdoorToggle: {
    category: CATEGORY.BottomPanel,
    action: 'change:outdoor',
    label: '{isOutdoor}',
  },
  bottomPanelAntennaButton: {
    category: CATEGORY.BottomPanel,
    action: 'click',
  },
  // Canvas events
  canvasAddMesh: {
    category: CATEGORY.Canvas,
    action: 'add:mesh',
  },
  canvasDeleteMesh: {
    category: CATEGORY.Canvas,
    action: 'delete:mesh',
  },
  canvasMoveDevice: {
    category: CATEGORY.Canvas,
    action: 'move:device',
    label: '{id}',
  },
  // other events
  toolsSelect: {
    category: CATEGORY.Tools,
    action: 'click:tool',
    label: '{tool}',
  },
  toolsAddMesh: {
    category: CATEGORY.Tools,
    action: 'add:mesh',
  },
  toolsDeleteMesh: {
    category: CATEGORY.Tools,
    action: 'delete:mesh',
  },
  clientFormAction: {
    category: CATEGORY.ClientForm,
    action: 'click',
    label: '{action}',
  },
  settingsSelect: {
    category: CATEGORY.Settings,
    action: 'settings:toggle',
  },
  settingsChange: {
    category: CATEGORY.Settings,
    action: 'settings:change',
    label: '{setting}:{newValue}',
  },
});

type EventType = keyof typeof events;

export const trackEvent = (type: EventType, data?: Record<string, number | string>) => {
  let error;
  if (!events[type]) {
    error = `unknown event: ${type}`;
  }

  const eventOptions = {
    nonInteraction: false,
    ...events[type],
  };

  if (data) {
    if (eventOptions.label) {
      // replace event label placeholders, i.e.: "{variant}:{channel}" with provided data values
      eventOptions.label = eventOptions.label.replace(/{(\w+)}/g, (allMatch: string, placeholder: string) => {
        if (data[placeholder] === undefined) {
          error = `unknown placeholder "${placeholder}" in event: ${type}`;
          return allMatch; // keep the placeholder unchanged
        }
        return data[placeholder].toString();
      });
    }

    if (typeof data.value !== 'undefined') {
      if (typeof data.value === 'number') {
        eventOptions.value = data.value;
      } else {
        error = `value is not contained in event: ${type}`;
      }
    }
  }

  if (error) {
    throwOrLog(error);
  }

  ReactGA.event(eventOptions);
};

export const trackEventDebounced = debounce(trackEvent, 1000);
